import { useMutation } from '@apollo/client';
import PhoneNumberPopup from '@components/Care/PhoneNumberPopup';
import CustomImage from '@components/common/CustomImage';
import { UserDataContext } from '@contexts/UserDataContext';
import { Box, Button, Card, Typography } from '@mui/material';
import { SUBSCRIBE_USER_TO_PLANT } from '@operations/plants/mutations';
import { UPDATE_USER } from '@operations/user/mutations';
import { useTranslation } from 'next-i18next';
import Image from 'next/image';
import { useRouter } from 'next/router';
import PropTypes from 'prop-types';
import { useContext, useState } from 'react';
import { SubscriptionSuccessful } from '../Message/messages';

const styles = {
  cardWrapper: {
    width: { xs: '180px', md: '220px', lg: '280px' },
    height: { xs: '180px', md: '220px', lg: '280px' },
    backgroundColor: 'secondary.linen',
    borderRadius: '20px',
    overflow: 'hidden',
    boxShadow: 'none',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    position: 'relative',
  },
  buttonWrapper: {
    position: 'absolute',
    bottom: '10px',
    left: '10px',
  },
};

export default function PlantCard({
  plantData: { id, name, scientific_name, image_url, objectID },
  queryId,
}) {
  const { query = {} } = useRouter();
  const { phone } = query;
  const [phoneToSubscribe, setPhoneToSubscribe] = useState(phone);
  const { t } = useTranslation('common');
  const [isSubscribed, setIsSubscribed] = useState(false);
  const [phoneNoPopup, setPhoneNoPopup] = useState(false);
  const { authUser, refetchUserData } = useContext(UserDataContext);

  const [updateUser] = useMutation(UPDATE_USER, {
    onCompleted: () => {
      refetchUserData();
    },
  });

  const [whatsappSubscribe] = useMutation(SUBSCRIBE_USER_TO_PLANT, {
    onCompleted: () => {
      SubscriptionSuccessful();
      setIsSubscribed(true);
    },
  });

  const whatsappShareHandler = () => {
    if (phoneToSubscribe) {
      whatsappSubscribe({
        variables: {
          phone: phoneToSubscribe.replace(' ', '+'),
          plantId: id,
          queryId,
          plant: {
            id,
            scientific_name,
            name,
            objectID,
          },
        },
      });
    } else {
      setPhoneNoPopup(true);
    }
  };

  const handlePhoneNoSubmit = async (phoneNumber) => {
    setPhoneNoPopup(false);
    setPhoneToSubscribe(phoneNumber);
    if (authUser) {
      await updateUser({
        variables: {
          phone: phoneNumber,
        },
      });
    }
    await whatsappShareHandler();
  };

  const buttonSubscribeText = {
    subscribe: t('subscribe_to_plant'),
    subscribed: t('subscribed'),
  };

  return (
    <>
      <Box>
        <Card sx={{ ...styles.cardWrapper }}>
          <CustomImage imageUrl={image_url} alt={name} />
          <Box sx={styles.buttonWrapper}>
            <Button
              variant="basic"
              color="white"
              onClick={whatsappShareHandler}
              disabled={isSubscribed}
              sx={{ height: '46px', borderRadius: '10px' }}
              startIcon={
                <Image
                  src="/assets/images/social/whatsapp_icon_2.png"
                  width={20}
                  height={20}
                  alt="Whatsapp"
                  layout="fixed"
                />
              }
            >
              {isSubscribed
                ? buttonSubscribeText.subscribed
                : buttonSubscribeText.subscribe}
            </Button>
          </Box>
        </Card>

        <Typography variant="h5" fontFamily="Inter" mt={2.5}>
          {name}
        </Typography>

        <Typography
          variant="h6"
          fontFamily="Inter"
          fontStyle="italic"
          color="text.secondaryPink"
          mt={0.5}
        >
          {scientific_name}
        </Typography>
      </Box>
      {phoneNoPopup && (
        <PhoneNumberPopup
          open={phoneNoPopup}
          handleClose={() => setPhoneNoPopup(false)}
          handleSubmit={handlePhoneNoSubmit}
        />
      )}
    </>
  );
}

PlantCard.defaultProps = {
  plantData: {
    image_url: '',
    name: '',
    scientific_name: '',
    id: '',
  },
  queryId: '',
};

PlantCard.propTypes = {
  plantData: PropTypes.shape({
    image_url: PropTypes.string,
    name: PropTypes.string,
    scientific_name: PropTypes.string,
    id: PropTypes.number,
  }),
  queryId: PropTypes.string,
};
